import { type HTMLAttributes } from 'react';

import { useRelativeTime } from '@/shared/hooks/useRelativeTime/useRelativeTime';
import { cn } from '@/shared/utils/cn/cn.util';

import { Tooltip, TooltipContent, TooltipTrigger } from '../Tooltip/Tooltip';

export interface RelativeTimeProps extends Omit<HTMLAttributes<HTMLElement>, 'children'> {
  /** An ISO instant, or null when the platform recorded none. */
  value: string | null | undefined;
}

/**
 * An instant, written the way a person reads one: "3 minutes ago".
 *
 * The exact instant is carried **three** ways, on purpose. `<time dateTime>` is
 * the machine-readable one. The tooltip is the sighted-mouse one. And an
 * `sr-only` span holds it unconditionally, because a tooltip reaches neither a
 * touch device nor a screen reader — the kit's own Tooltip doc says a tooltip is
 * a hint and never the only place information lives, and an audit trail is
 * exactly the kind of fact that must not depend on a hover.
 *
 * With nothing recorded it renders a plain `—` and **no** `<time>`: an empty
 * `datetime` is a machine-readable claim about nothing.
 */
export function RelativeTime({ value, className, ...props }: RelativeTimeProps) {
  const { iso, relative, absolute } = useRelativeTime(value);

  if (iso === null) {
    return (
      <span className={className} {...props}>
        {relative}
      </span>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <time dateTime={iso} className={cn('cursor-default', className)} {...props}>
          {relative}
          <span className="sr-only"> ({absolute})</span>
        </time>
      </TooltipTrigger>
      <TooltipContent>{absolute}</TooltipContent>
    </Tooltip>
  );
}
